import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { styled } from '@mui/material/styles'
import { type BasePuiDialogProps, PuiDialog } from '@pbt/pbt-ui-components'

import { useSwapReactiveRxMutation } from '~/api/graphql/generated/types'
import FeatureToggle from '~/constants/featureToggle'
import { getSwapProductTrackingData } from '~/constants/swapProductAnalytics'
import { getFeatureToggle } from '~/store/reducers/constants'
import type { Order } from '~/types'
import type { Prescription } from '~/types/entities/prescriptions'
import { trackEvent, TrackingEvent } from '~/utils/trackingProvider'

import { RhapConfirmCloseDialog } from '../RhapConfirmCloseDialog'
import { EditPrescriptionStep } from './EditPrescriptionStep'
import { FinalizePrescriptionStep } from './finalizePrescription/FinalizePrescriptionStep'
import { SwapPatientStep } from './patientStep/SwapPatientStep'
import SwapProductStep from './SwapProductStep'
import { SwapProductDialogSteps } from './types'
import {
  convertPrescriptionToRxInputForSwap,
  sanitizePrescriptionForSwap,
} from './utils'

const StyledPuiDialog = styled(PuiDialog)`
  .MuiPaper-root {
    width: ${({ step }: { step: SwapProductDialogSteps }) => {
      switch (step) {
        case SwapProductDialogSteps.EDIT_RX:
        case SwapProductDialogSteps.CHANGE_PRODUCT:
          return '1000px'
        default:
          return '800px'
      }
    }};
    max-width: ${({ step }: { step: SwapProductDialogSteps }) => {
      switch (step) {
        case SwapProductDialogSteps.EDIT_RX:
        case SwapProductDialogSteps.CHANGE_PRODUCT:
          return '1000px'
        default:
          return '800px'
      }
    }};
    height: 800px;
    min-height: 800px;
    overflow-y: unset;
  }

  .MuiDialogContent-root {
    padding: ${({ step }: { step: SwapProductDialogSteps }) =>
      step === SwapProductDialogSteps.FINALIZE ? '16px 24px' : '0'};
    min-height: ${({ step }: { step: SwapProductDialogSteps }) =>
      step === SwapProductDialogSteps.FINALIZE ? '600px' : '100%'};
  }
`
const RejectionReason: Record<
  Exclude<SwapProductDialogSteps, SwapProductDialogSteps.FINALIZE>,
  string
> = {
  [SwapProductDialogSteps.CHANGE_PRODUCT]: 'Wrong product',
  [SwapProductDialogSteps.CHANGE_PATIENT]: 'Wrong pet',
  [SwapProductDialogSteps.EDIT_RX]: 'Wrong count',
}

interface SwapProductDialogProps extends BasePuiDialogProps {
  initialPrescription: Prescription
  initialStep: Exclude<SwapProductDialogSteps, SwapProductDialogSteps.FINALIZE>
}

export function SwapProductDialog({
  initialPrescription,
  initialStep,
  open,
  onClose,
}: SwapProductDialogProps) {
  const { t } = useTranslation('Dialogs')
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] =
    useState<SwapProductDialogSteps>(initialStep)
  const [currentPatientId, setCurrentPatientId] = useState(
    initialPrescription.patientId,
  )
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([
    sanitizePrescriptionForSwap(initialPrescription),
  ])
  const [editingPrescriptionIndex, setEditingPrescriptionIndex] =
    useState<number>(0)
  const [confirmCloseDialogOpen, setConfirmCloseDialogOpen] = useState(false)
  const [swapReactiveRx, { loading: swapLoading, error: swapError }] =
    useSwapReactiveRxMutation()
  const [hasSwapError, setHasSwapError] = useState(false)
  const isFoodCatalogEnabled = useSelector(
    getFeatureToggle(FeatureToggle.FOOD_CATALOG),
  )

  const handleClose = () => {
    if (currentStep === SwapProductDialogSteps.CHANGE_PRODUCT) {
      trackEvent(
        TrackingEvent.SWAP_PRODUCT_SEARCH_CANCEL,
        getSwapProductTrackingData(
          'SWAP_PRODUCT_SEARCH_CANCEL',
          initialPrescription,
        ),
      )
    }

    if (
      currentStep === SwapProductDialogSteps.EDIT_RX &&
      initialStep === SwapProductDialogSteps.CHANGE_PRODUCT
    ) {
      trackEvent(
        TrackingEvent.SWAP_PRODUCT_RX_FORM_CANCEL,
        getSwapProductTrackingData(
          'SWAP_PRODUCT_RX_FORM_CANCEL',
          initialPrescription,
        ),
      )
    }

    if (
      currentStep === SwapProductDialogSteps.FINALIZE &&
      initialStep === SwapProductDialogSteps.CHANGE_PRODUCT
    ) {
      trackEvent(
        TrackingEvent.SWAP_PRODUCT_FINALIZE_CANCEL,
        getSwapProductTrackingData(
          'SWAP_PRODUCT_FINALIZE_CANCEL',
          initialPrescription,
        ),
      )
    }

    if (
      currentStep === SwapProductDialogSteps.FINALIZE &&
      initialStep === SwapProductDialogSteps.EDIT_RX
    ) {
      trackEvent(
        TrackingEvent.SWAP_COUNT_FINALIZE_CANCEL,
        getSwapProductTrackingData(
          'SWAP_COUNT_FINALIZE_CANCEL',
          initialPrescription,
        ),
      )
    }

    if (currentStep === SwapProductDialogSteps.CHANGE_PATIENT) {
      trackEvent(
        TrackingEvent.SWAP_PATIENT_SEARCH_CANCEL,
        getSwapProductTrackingData(
          'SWAP_PATIENT_SEARCH_CANCEL',
          initialPrescription,
        ),
      )
    }

    if (
      currentStep === SwapProductDialogSteps.FINALIZE &&
      initialStep === SwapProductDialogSteps.CHANGE_PATIENT
    ) {
      trackEvent(
        TrackingEvent.SWAP_PATIENT_FINALIZE_CANCEL,
        getSwapProductTrackingData(
          'SWAP_PATIENT_FINALIZE_CANCEL',
          initialPrescription,
        ),
      )
    }

    setConfirmCloseDialogOpen(true)
  }

  const handleCancelClose = () => {
    if (currentStep === SwapProductDialogSteps.CHANGE_PRODUCT) {
      trackEvent(
        TrackingEvent.SWAP_PRODUCT_SEARCH_CANCEL_GO_BACK,
        getSwapProductTrackingData(
          'SWAP_PRODUCT_SEARCH_CANCEL_GO_BACK',
          initialPrescription,
        ),
      )
    }

    setConfirmCloseDialogOpen(false)
  }

  const handleCancelDialogClose = () => {
    if (currentStep === SwapProductDialogSteps.CHANGE_PRODUCT) {
      trackEvent(
        TrackingEvent.SWAP_PRODUCT_SEARCH_CANCEL_CLOSE,
        getSwapProductTrackingData(
          'SWAP_PRODUCT_SEARCH_CANCEL_CLOSE',
          initialPrescription,
        ),
      )
    }

    setConfirmCloseDialogOpen(false)
  }

  const handleConfirmClose = () => {
    if (currentStep === SwapProductDialogSteps.CHANGE_PRODUCT) {
      trackEvent(
        TrackingEvent.SWAP_PRODUCT_SEARCH_CANCEL_DISCARD,
        getSwapProductTrackingData(
          'SWAP_PRODUCT_SEARCH_CANCEL_DISCARD',
          initialPrescription,
        ),
      )
    }

    onClose?.()
  }

  const trackFinalize = () => {
    if (
      currentStep === SwapProductDialogSteps.FINALIZE &&
      initialStep === SwapProductDialogSteps.CHANGE_PRODUCT
    ) {
      trackEvent(
        TrackingEvent.SWAP_PRODUCT_FINALIZE_CONFIRM,
        getSwapProductTrackingData(
          'SWAP_PRODUCT_FINALIZE_CONFIRM',
          initialPrescription,
        ),
      )
    }

    if (
      currentStep === SwapProductDialogSteps.FINALIZE &&
      initialStep === SwapProductDialogSteps.EDIT_RX
    ) {
      trackEvent(
        TrackingEvent.SWAP_COUNT_FINALIZE_CONFIRM,
        getSwapProductTrackingData(
          'SWAP_COUNT_FINALIZE_CONFIRM',
          initialPrescription,
        ),
      )
    }

    if (
      currentStep === SwapProductDialogSteps.FINALIZE &&
      initialStep === SwapProductDialogSteps.CHANGE_PATIENT
    ) {
      trackEvent(
        TrackingEvent.SWAP_PATIENT_FINALIZE_CONFIRM,
        getSwapProductTrackingData(
          'SWAP_PATIENT_FINALIZE_CONFIRM',
          initialPrescription,
        ),
      )
    }
  }

  const editPrescriptionAtIndex = (index: number) => {
    setEditingPrescriptionIndex(index)
    setCurrentStep(SwapProductDialogSteps.EDIT_RX)
  }

  const deletePrescriptionAtIndex = (index: number) => {
    setPrescriptions((prev) => prev.filter((_, i) => i !== index))
  }

  const redirectToPatientRecords = () => {
    const { clientId } = initialPrescription
    const patientId = currentPatientId

    navigate(`/client/${clientId}/patient/${patientId}/records`, {
      state: { showSwapProductSuccess: true },
    })
  }

  const handleFinalize = async () => {
    try {
      await swapReactiveRx({
        variables: {
          prescriptionId: initialPrescription.id,
          input: {
            declineReactiveRx: {
              ...convertPrescriptionToRxInputForSwap(
                initialPrescription,
                isFoodCatalogEnabled,
              ),
              rejectionReason: RejectionReason[initialStep],
            },
            approveActiveRxs: prescriptions.map((prescription) =>
              convertPrescriptionToRxInputForSwap(
                prescription,
                isFoodCatalogEnabled,
              ),
            ),
          },
        },
        onCompleted: () => {
          trackFinalize()
          onClose?.()
          redirectToPatientRecords()
        },
        onError: () => {
          setHasSwapError(true)
        },
      })
    } catch (err) {
      setHasSwapError(true)
    }
  }

  const titleComponents: Record<SwapProductDialogSteps, string | undefined> = {
    [SwapProductDialogSteps.FINALIZE]: t('SWAP_PRODUCT_DIALOG.FINALIZE_TITLE'),
    [SwapProductDialogSteps.CHANGE_PRODUCT]: t(
      'SWAP_PRODUCT_DIALOG.CHANGE_PRODUCT_TITLE',
    ),
    [SwapProductDialogSteps.CHANGE_PATIENT]: t(
      'SWAP_PRODUCT_DIALOG.CHANGE_PATIENT_TITLE',
    ),
    [SwapProductDialogSteps.EDIT_RX]: undefined,
  }

  return (
    <>
      <StyledPuiDialog
        open={open}
        step={currentStep}
        title={titleComponents[currentStep as SwapProductDialogSteps]}
        onClose={handleClose}
      >
        {currentStep === SwapProductDialogSteps.FINALIZE && (
          <FinalizePrescriptionStep
            hasError={Boolean(swapError || hasSwapError)}
            initialStep={initialStep}
            isSwapLoading={swapLoading}
            prescriptions={prescriptions}
            onDeletePrescription={deletePrescriptionAtIndex}
            onEditPrescription={editPrescriptionAtIndex}
            onProceed={handleFinalize}
          />
        )}
        {currentStep === SwapProductDialogSteps.CHANGE_PRODUCT && (
          <SwapProductStep
            prescription={initialPrescription}
            onProceed={(newOrder: Order) => {
              setCurrentStep(SwapProductDialogSteps.EDIT_RX)
              setPrescriptions(
                prescriptions.map((prescription, index) =>
                  index === editingPrescriptionIndex
                    ? (newOrder as Prescription)
                    : prescription,
                ),
              )
            }}
          />
        )}
        {currentStep === SwapProductDialogSteps.CHANGE_PATIENT && (
          <SwapPatientStep
            prescription={prescriptions[editingPrescriptionIndex]}
            onPatientChange={(patientId) => {
              setCurrentPatientId(patientId)
            }}
            onProceed={(newPrescription) => {
              setPrescriptions((prev) =>
                prev.map((prescription, i) =>
                  i === editingPrescriptionIndex
                    ? newPrescription
                    : prescription,
                ),
              )
              setCurrentStep(SwapProductDialogSteps.EDIT_RX)
            }}
          />
        )}
        {currentStep === SwapProductDialogSteps.EDIT_RX && (
          <EditPrescriptionStep
            onProceed={() => setCurrentStep(SwapProductDialogSteps.FINALIZE)}
          />
        )}
      </StyledPuiDialog>
      <RhapConfirmCloseDialog
        bodyText={t('SWAP_PRODUCT_CONFIRM_CLOSE_DIALOG.BODY_TEXT')}
        open={confirmCloseDialogOpen}
        onCancelClose={handleCancelClose}
        onClose={handleCancelDialogClose}
        onConfirmClose={handleConfirmClose}
      />
    </>
  )
}
